import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Users, BookOpen, TrendingUp, Zap, CreditCard, Tag, ArrowUpDown, ArrowUp, ArrowDown, Clock, Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCreditSystem } from "@/hooks/useCreditSystem";
import { useTestClock } from "@/contexts/TestClockContext";
import { safeLog } from '@/utils/safeLogger';
import { formatActionLabel } from '@/lib/actionLabels';

interface UserTransaction {
  id: string;
  action_type: string;
  baseline_credits: number;
  openai_usage_credits: number;
  final_credits: number;
  credits_before: number;
  credits_after: number;
  transaction_id: string;
  created_at: string;
  context_metadata?: {
    totalTokens?: number;
    features?: {
      extraction?: boolean;
      smartCategories?: boolean;
      smartNotes?: boolean;
    };
    tier?: string;
    package?: string;
    expired_additional_credits?: number;
  };
}

export function UserCreditUsage() {
  const { isTestMode, getCurrentTime, clockOffset } = useTestClock();
  const [transactions, setTransactions] = useState<UserTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState<'created_at' | 'credits_before' | 'final_credits' | 'credits_after'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const itemsPerPage = 50;
  
  const { stats } = useCreditSystem();
  const { totalUsed, currentBalance } = stats;

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    (async () => {
      await loadUserTransactions();

      // Scope realtime subscription to current user. Without filter, Realtime CDC
      // could broadcast other users' credit_transactions to this channel.
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const channel = supabase
        .channel(`user-credit-changes-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'credit_transactions',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            console.log('[UserCreditUsage] New transaction:', payload);
            const newTxn = payload.new as UserTransaction;
            setTransactions(prev => [newTxn, ...prev].slice(0, itemsPerPage));
            setTimeout(() => loadUserTransactions(), 500);
          }
        )
        .subscribe();

      cleanup = () => {
        supabase.removeChannel(channel);
      };
    })();

    return () => {
      cleanup?.();
    };
  }, []);

  async function loadUserTransactions() {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch total count
      const { count } = await supabase
        .from('credit_transactions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setTotalCount(count || 0);

      // Fetch paginated transactions
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      const { data: txns, error } = await supabase
        .from('credit_transactions')
        .select('id, action_type, baseline_credits, openai_usage_credits, final_credits, credits_before, credits_after, transaction_id, created_at, context_metadata')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('Error loading transactions:', error);
        return;
      }

      setTransactions(txns as UserTransaction[] || []);

    } catch (error) {
      console.error('Error loading user transactions:', error);
    } finally {
      setLoading(false);
    }
  }

  const getFeatureBadges = (txn: UserTransaction) => {
    // For topup and subscription - show as before
    if (txn.action_type === 'topup') {
      const packageType = txn.context_metadata?.package || 'Credits';
      return (
        <Badge className="gap-1 bg-purple/15 text-purple border border-purple/30 hover:bg-purple/20">
          <TrendingUp className="h-3 w-3" />
          Top-up ({packageType})
        </Badge>
      );
    }

    if (txn.action_type === 'subscription_purchase') {
      const tier = txn.context_metadata?.tier || '';
      const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
      return (
        <Badge className="gap-1 bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
          <Zap className="h-3 w-3" />
          New Subscription ({tierLabel})
        </Badge>
      );
    }

    if (txn.action_type === 'subscription_renewal') {
      const tier = txn.context_metadata?.tier || '';
      const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
      const expiredCredits = txn.context_metadata?.expired_additional_credits;

      // Always show the renewal badge
      const badges = [
        <Badge key="renewal" className="gap-1 bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
          <Zap className="h-3 w-3" />
          Subscription Renewed ({tierLabel})
        </Badge>,
      ];

      // Add expiration badge if credits expired
      if (expiredCredits && expiredCredits > 0) {
        badges.push(
          <Badge key="expired" className="gap-1 bg-warning/15 text-warning border border-warning/30 hover:bg-warning/20">
            <Clock className="h-3 w-3" />
            Credits Expired ({tierLabel})
          </Badge>
        );
      }

      return <>{badges}</>;
    }

    // Plan renewal expiration
    if (txn.action_type === 'plan_renewal_expiration') {
      return (
        <Badge className="gap-1 bg-warning/15 text-warning border border-warning/30 hover:bg-warning/20">
          <Zap className="h-3 w-3" />
          Additional Credits Expired
        </Badge>
      );
    }

    // Plan change expiration
    if (txn.action_type === 'plan_change_expiration') {
      return (
        <Badge className="gap-1 bg-purple/15 text-purple border border-purple/30 hover:bg-purple/20">
          <Zap className="h-3 w-3" />
          Credits Expired (Plan Change)
        </Badge>
      );
    }

    // For contact_processing - show multiple badges based on features
    if (txn.action_type === 'contact_processing' && txn.context_metadata?.features) {
      const features = txn.context_metadata.features;
      const badges = [];

      if (features.extraction) {
        badges.push(
          <Badge key="extraction" variant="default" className="gap-1">
            <Users className="h-3 w-3" />
            Contact Extracted
          </Badge>
        );
      }

      if (features.smartCategories) {
        badges.push(
          <Badge key="categories" variant="secondary" className="gap-1">
            <Tag className="h-3 w-3" />
            Smart Categories
          </Badge>
        );
      }

      if (features.smartNotes) {
        badges.push(
          <Badge key="notes" variant="outline" className="gap-1">
            <BookOpen className="h-3 w-3" />
            Smart Notes
          </Badge>
        );
      }

      return <div className="flex gap-1 flex-wrap">{badges}</div>;
    }

    // Legacy support for old action types
    if (txn.action_type === 'contact_extraction') {
      return (
        <Badge variant="default" className="gap-1">
          <Users className="h-3 w-3" />
          Contact Extracted
        </Badge>
      );
    }

    if (txn.action_type === 'smart_notes' || txn.action_type === 'smart_notes_research') {
      return (
        <Badge variant="outline" className="gap-1">
          <BookOpen className="h-3 w-3" />
          Smart Notes
        </Badge>
      );
    }

    // Welcome bonus
    if (txn.action_type === 'welcome_bonus') {
      return (
        <Badge className="gap-1 bg-success/15 text-success border border-success/30 hover:bg-success/20">
          <Gift className="h-3 w-3" />
          Welcome Credits
        </Badge>
      );
    }

    // Review reward
    if (txn.action_type === 'review_reward') {
      return (
        <Badge className="gap-1 bg-success/15 text-success border border-success/30 hover:bg-success/20">
          <Gift className="h-3 w-3" />
          Review Reward
        </Badge>
      );
    }

    // Auto-refund (system error) or admin-issued error refund
    if (txn.action_type === 'auto_refund' || txn.action_type === 'error_refund') {
      return (
        <Badge className="gap-1 bg-success/15 text-success border border-success/30 hover:bg-success/20">
          <Gift className="h-3 w-3" />
          {txn.action_type === 'auto_refund' ? 'Error Refund' : 'Refund'}
        </Badge>
      );
    }

    // Admin grant — can be positive or negative
    if (txn.action_type === 'admin_grant') {
      const isPositive = (txn.final_credits ?? 0) > 0;
      return (
        <Badge className={cn(
          "gap-1 border",
          isPositive
            ? "bg-success/15 text-success border-success/30 hover:bg-success/20"
            : "bg-warning/15 text-warning border-warning/30 hover:bg-warning/20"
        )}>
          <Gift className="h-3 w-3" />
          {isPositive ? 'Admin Grant' : 'Admin Adjustment'}
        </Badge>
      );
    }

    // Default fallback
    return (
      <Badge variant="outline" className="gap-1">
        <CreditCard className="h-3 w-3" />
        {formatActionLabel(txn.action_type)}
      </Badge>
    );
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 ml-1" /> : 
      <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      let aValue: number | Date;
      let bValue: number | Date;

      if (sortField === 'created_at') {
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
      } else {
        aValue = a[sortField] || 0;
        bValue = b[sortField] || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transactions, sortField, sortDirection]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Credit History</CardTitle>
          <CardDescription>Loading transactions...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs text-muted-foreground">
          Showing <span className="font-medium text-foreground">{transactions.length}</span> of{" "}
          <span className="font-medium text-foreground">{totalCount}</span> transactions
        </p>
        {isTestMode && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
            <Clock className="h-3 w-3" />
            Simulated: {format(getCurrentTime(), 'PPP HH:mm')}
          </span>
        )}
      </div>
      <div>
        {transactions.length === 0 ? (
          <div className="text-center text-muted-foreground py-12 text-sm">
            No transactions yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Date
                      <SortIcon field="created_at" />
                    </div>
                  </TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('credits_before')}
                  >
                    <div className="flex items-center justify-end">
                      Before
                      <SortIcon field="credits_before" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('final_credits')}
                  >
                    <div className="flex items-center justify-end">
                      Amount
                      <SortIcon field="final_credits" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('credits_after')}
                  >
                    <div className="flex items-center justify-end">
                      After
                      <SortIcon field="credits_after" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTransactions.map((txn) => {
                  const isCredit = txn.action_type === 'topup' ||
                                 txn.action_type === 'subscription_purchase' ||
                                 txn.action_type === 'welcome_bonus' ||
                                 txn.action_type === 'review_reward' ||
                                 txn.action_type === 'auto_refund' ||
                                 txn.action_type === 'error_refund' ||
                                 (txn.action_type === 'admin_grant' && (txn.final_credits ?? 0) > 0) ||
                                 (txn.action_type === 'subscription_renewal' &&
                                  (!txn.context_metadata?.expired_additional_credits ||
                                   txn.context_metadata.expired_additional_credits === 0));
                  const isExpiration = txn.action_type === 'plan_renewal_expiration' ||
                                      txn.action_type === 'plan_change_expiration' ||
                                      (txn.action_type === 'subscription_renewal' &&
                                       txn.context_metadata?.expired_additional_credits &&
                                       txn.context_metadata.expired_additional_credits > 0);
                  return (
                    <TableRow key={txn.id}>
                      <TableCell className={cn(
                        "text-sm",
                        isTestMode && "text-primary font-medium"
                      )}>
                        {format(
                          isTestMode 
                            ? new Date(new Date(txn.created_at).getTime() + clockOffset)
                            : new Date(txn.created_at),
                          'MMM dd, yyyy HH:mm'
                        )}
                        {isTestMode && <span className="text-xs ml-1">(sim)</span>}
                      </TableCell>
                      <TableCell>
                        {getFeatureBadges(txn)}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {txn.credits_before ?? '—'}
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-medium tabular-nums",
                        isCredit
                          ? "text-success"
                          : isExpiration
                          ? "text-warning"
                          : "text-destructive"
                      )}>
                        {isCredit
                          ? `+${Math.round(txn.final_credits)}`
                          : isExpiration
                          ? `-${Math.abs(Math.round(txn.final_credits))}`
                          : `-${Math.abs(Math.round(txn.final_credits))}`
                        }
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {txn.credits_after ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {totalCount > itemsPerPage && (
          <div className="px-5 py-3 border-t border-border/60">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      setCurrentPage(p => Math.max(1, p - 1));
                      loadUserTransactions();
                    }}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    );
                  })
                  .map((page, idx, arr) => (
                    <span key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => {
                            setCurrentPage(page);
                            setTimeout(loadUserTransactions, 0);
                          }}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    </span>
                  ))
                }

                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      setCurrentPage(p => Math.min(totalPages, p + 1));
                      loadUserTransactions();
                    }}
                    className={currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
}
